"use client";

import { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { usePathname } from "next/navigation";
import { toast } from "@jasperlabs/ui";
import { useSession } from "@/contexts/session";
import { useLogger } from "@/contexts/logger";
import useAuthenticatedUser from "@/components/utilities/useAuthenticatedUser";
import {
	applicationProgressScopeVar,
	applicationProgressScopeStalledVar,
	discardUnboundApplicationProgress,
	discardUnclaimedSignedOutProgress,
	releaseStalledApplicationProgressScope,
	setApplicationProgressScope,
} from "./applicationProgressVar";

// A scope that has not resolved by now is not going to: the account query has
// had time to run and retry. The same allowance the application flow gives an
// investing-entity switch to verify (`PERMISSION_VERIFY_WATCHDOG_MS`).
const ACCOUNT_RESOLVE_WATCHDOG_MS = 10_000;

/**
 * Keeps the application-progress scope pointed at the signed-in account.
 *
 * `useAuthenticatedUser` reads the account from the `Account` singleton and,
 * unlike a one-field cache read, self-heals: its `ME` query is incomplete in the
 * primed cache, so it fetches rather than resolving to the empty placeholder.
 *
 * A resolved scope is held across the window where the cached account id is
 * momentarily blank — minting a new session token resets the Apollo store, and
 * every investing-entity switch mints one — but only for the token it was
 * resolved against. Holding it for any token would let a token swapped in by
 * another tab inherit the previous account's records. Progress written while
 * the scope is unknown is buffered rather than refused, so nothing is lost
 * across that window either way.
 */
export default function ApplicationProgressScope() {
	const { hasAuthenticatedSession, sessionToken } = useSession();
	const account = useAuthenticatedUser();
	const accountId: string | null = account?.id ?? null;
	const { captureMessage } = useLogger();
	// Read through a ref: the report wants the path at the moment of the stall,
	// but a route change must not restart the watchdog. Published on commit —
	// the only reader is the watchdog timer, ten seconds later.
	const pathname = usePathname();
	const pathnameRef = useRef(pathname);
	useEffect(() => {
		pathnameRef.current = pathname;
	}, [pathname]);

	// The session token the standing scope belongs to.
	const scopeTokenRef = useRef<string | null>(null);
	// Whether the cached account id still belongs to a token that has been
	// replaced. Replacing a token schedules an Apollo store reset, and until that
	// lands `useAuthenticatedUser` keeps answering with the previous token's
	// account — accepting it would re-admit the very leak the token check closes.
	//
	// State rather than a ref, and cleared by the reset itself rather than by
	// watching for the cached id to blank. The account is a `keyFields: []`
	// singleton whose refetch restores the same id, so on an investing-entity
	// switch that blank is never delivered and nothing would re-run this effect.
	const [awaitingStoreReset, setAwaitingStoreReset] = useState(false);
	const client = useApolloClient();
	useEffect(
		() =>
			client.onResetStore(async () => {
				// The cache the previous token filled is gone, so whatever answers
				// from here was fetched with the token that replaced it.
				setAwaitingStoreReset(false);
			}),
		[client],
	);

	useEffect(() => {
		/* eslint-disable react-hooks/set-state-in-effect -- scope must follow session token, store reset, and cached account id in one pass */
		if (!hasAuthenticatedSession) {
			scopeTokenRef.current = null;
			setAwaitingStoreReset(false);
			setApplicationProgressScope(null);
			return;
		}

		let isReplacement = false;
		if (sessionToken !== scopeTokenRef.current) {
			// A token that replaces another triggers the store reset; the first
			// token of a session does not, so its cached account is already its own.
			isReplacement = Boolean(scopeTokenRef.current);
			setAwaitingStoreReset(isReplacement);
			scopeTokenRef.current = sessionToken;
			if (isReplacement) {
				discardUnclaimedSignedOutProgress();
			}
			discardUnboundApplicationProgress();
			setApplicationProgressScope(undefined);
		}

		// `isReplacement` as well as the state: the setter above does not change
		// this render's `awaitingStoreReset`, so reading only that would scope the
		// replacing token to the previous one's cached account before the wait
		// even begins.
		if (isReplacement || awaitingStoreReset) {
			return;
		}

		if (accountId) {
			setApplicationProgressScope(accountId, sessionToken);
		}
		/* eslint-enable react-hooks/set-state-in-effect */
	}, [hasAuthenticatedSession, accountId, sessionToken, awaitingStoreReset]);

	// An authenticated session whose account never resolves keeps working, but
	// nothing it does is persisted past a reload. That is silent without this.
	const hasReportedRef = useRef(false);
	useEffect(() => {
		// Deliberately not gated on `hasReportedRef`: every unresolved episode
		// needs its own timer, because releasing the flow is what stops it waiting.
		// A later sign-in whose account query also fails would otherwise sit on a
		// loading placeholder for good, the stall having been cleared by the
		// successful resolution in between.
		// A cached id that belongs to a replaced token is not a resolution: the
		// reset that clears it is itself something that may never land, so the
		// watchdog is armed across that wait too.
		if (!hasAuthenticatedSession || (accountId && !awaitingStoreReset)) {
			return undefined;
		}
		const timer = setTimeout(() => {
			if (
				applicationProgressScopeVar() !== undefined ||
				applicationProgressScopeStalledVar()
			) {
				return;
			}
			// Stop the flow waiting on an account that is not coming, dropping the
			// progress kept for it: this token may belong to someone else. Told the
			// same way the flow's own watchdog tells it: the flow goes on, but
			// nothing it does from here is saved past a reload.
			releaseStalledApplicationProgressScope(sessionToken);
			toast.error("We couldn't load your account details. Please try again.");
			// One report per page session is enough; the condition is the same one.
			if (hasReportedRef.current) {
				return;
			}
			hasReportedRef.current = true;
			captureMessage("Application progress scope unresolved", {
				level: "warning",
				tags: {
					event_type: "progress_scope_unresolved",
					pathname: pathnameRef.current ?? "(unknown)",
				},
			});
		}, ACCOUNT_RESOLVE_WATCHDOG_MS);
		return () => clearTimeout(timer);
		// `sessionToken` restarts the timer: a token that replaces another while the
		// account is still unresolved must get the full wait, not the remainder of
		// its predecessor's.
	}, [
		hasAuthenticatedSession,
		accountId,
		awaitingStoreReset,
		sessionToken,
		captureMessage,
	]);

	return null;
}
